import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import * as React from 'react';
import type { NumericFormatProps } from 'react-number-format';
import { NumericFormat } from 'react-number-format';
import { useAppConfig } from '../../features/acl/acl-provider.tsx';
import { useLocale } from '../../lib/hooks/use-locale.ts';
import { cn } from '../../lib/utils.ts';
import SmallSpinner from './small-spinner.tsx';

const inputVariants = cva(
    'text-right border-input ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex w-full rounded-md border bg-transparent text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
    {
        variants: {
            variant: {
                default: 'bg-white',
            },
            size2: {
                default: 'h-8 px-2 py-2 text-sm',
                sm: 'h-6 px-2.5 text-sm',
            },
            hasLeftIcon: {
                true: 'pl-10',
            },
            errorBorder: {
                true: 'border-red-500',
            },
        },
        compoundVariants: [
            {
                hasLeftIcon: true,
                size2: 'sm',
                class: 'pl-9',
            },
        ],
        defaultVariants: {
            variant: 'default',
            size2: 'default',
        },
    },
);

export type InputProps = {
    loading?: boolean;
    errorBorder?: boolean;
    selectTextOnClick?: boolean;
} & NumericFormatProps &
    VariantProps<typeof inputVariants>;

export const InputNumber = ({
    selectTextOnClick = true,
    className,
    errorBorder = false,
    size2,
    type,
    loading,
    disabled,
    onClick,
    onBlur,
    onValueChange,
    ...props
}: InputProps) => {
    const { locale } = useLocale();
    const { defaultCurrency: currency } = useAppConfig();

    const inputLocaleConfig = getNumberFormat({
        locale,
        currency,
    });

    const combinedClassName = cn(inputVariants({ size2, errorBorder }), className);

    const focusedRef = React.useRef<string | null>(null);

    return (
        <div className="relative flex w-full ">
            <NumericFormat
                disabled={disabled || loading}
                className={cn('bg-yello relative flex w-full rounded-md', combinedClassName)}
                onClick={
                    selectTextOnClick
                        ? (e: React.MouseEvent<HTMLInputElement>) => {
                              if (onClick) {
                                  onClick(e);
                              }
                              if (e && e.target && e.currentTarget) {
                                  if (focusedRef.current === e.currentTarget.name) {
                                      return;
                                  }
                                  const target = e.target as HTMLInputElement;
                                  setTimeout(() => {
                                      target.select();
                                  }, 0);
                                  focusedRef.current = e.currentTarget.name;
                              }
                          }
                        : onClick
                }
                onBlur={
                    selectTextOnClick
                        ? (e) => {
                              focusedRef.current = null;
                              onBlur?.(e);
                          }
                        : onBlur
                }
                onValueChange={(values, sourceInfo) => {
                    if (sourceInfo.source === 'event') {
                        onValueChange?.(values, sourceInfo);
                    }
                }}
                thousandSeparator={inputLocaleConfig.groupSeparator}
                decimalSeparator={inputLocaleConfig.decimalSeparator}
                {...props}
            />
            {loading && (
                <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center rounded-md bg-neutral-100/70">
                    <SmallSpinner className="opacity-100" />
                </div>
            )}
        </div>
    );
};

type InputCurrencyProps = InputProps;

export const InputCurrency = React.forwardRef<typeof NumericFormat, InputCurrencyProps>(
    ({ className, ...props }) => {
        const { locale } = useLocale();
        const { defaultCurrency: currency } = useAppConfig();

        const inputLocaleConfig = getNumberFormat({
            locale,
            currency,
        });

        return (
            <InputNumber
                className={className}
                {...props}
                prefix={
                    props.prefix ? `${props.prefix}${inputLocaleConfig.prefix}` : inputLocaleConfig.prefix
                }
                suffix={
                    props.suffix ? `${inputLocaleConfig.suffix}${props.suffix}` : inputLocaleConfig.suffix
                }
            />
        );
    },
);
InputCurrency.displayName = 'InputCurrency';

type NumberFormat = {
    currencySymbol: string;
    groupSeparator: string;
    decimalSeparator: string;
    prefix: string;
    suffix: string;
};

type IntlConfig = {
    locale: string;
    currency: string;
};

const getNumberFormat = (intlConfig?: IntlConfig): NumberFormat => {
    const { locale, currency } = intlConfig ?? {};
    const numberFormatter = locale
        ? new Intl.NumberFormat(locale, currency ? { currency, style: 'currency' } : undefined)
        : new Intl.NumberFormat();

    const config: NumberFormat = {
        currencySymbol: '',
        groupSeparator: '',
        decimalSeparator: '',
        prefix: '',
        suffix: '',
    };

    for (const [i, part] of numberFormatter.formatToParts(1000.1).entries()) {
        if (part.type === 'currency') {
            if (i === 0) {
                config.currencySymbol = part.value;
                config.prefix = `${part.value} `;
            } else {
                config.currencySymbol = part.value;
                config.suffix = ` ${part.value}`;
            }
        } else if (part.type === 'group') {
            config.groupSeparator = part.value;
        } else if (part.type === 'decimal') {
            config.decimalSeparator = part.value;
        }
    }

    return config;
};
